import React, { useCallback, useMemo } from 'react';

import { i18n } from '../../../../../../shared/helpers/i18n';
import { countMetrika } from '../../../../../../shared/helpers/metrika';
import { useActions } from '../../../../../../shared/hooks';
import { stopCloudRecordingAction } from '../../../../features/cloud-recording';
import { stopRecordConferenceAction } from '../../../record/slice';
import { ToolbarButton } from '../toolbar-button';
import styles from './styles.css';
import { RecordingPlace, useStopRecordingButtonContent } from './use-stop-recording-button-content';

type StopRecordingAction = typeof stopRecordConferenceAction | typeof stopCloudRecordingAction;

const placeMap: Record<RecordingPlace, StopRecordingAction> = {
    local: stopRecordConferenceAction,
    cloud: stopCloudRecordingAction,
} as const;

type Props = {
    recordingPlace: RecordingPlace;
};

export function BaseStopRecordingButton({ recordingPlace }: Props) {
    const { isHovered, content, buttonProps } = useStopRecordingButtonContent(recordingPlace);

    const action = useMemo(() => placeMap[recordingPlace], [recordingPlace]);
    const actions = useActions({ action });
    const onClick = useCallback(() => {
        actions.action(undefined);
        countMetrika('meeting_screen', 'toolbar', `${recordingPlace}_recording_stop`);
    }, [actions, recordingPlace]);

    const title = useMemo(() => i18n('toolbar', `${recordingPlace}-recording-stop`), [recordingPlace]);

    const iconName = isHovered ? 'recordStop' : 'pulseRecordingDot';

    return (
        <ToolbarButton
            className={styles.stopButton}
            iconName={iconName}
            onClick={onClick}
            content={content}
            title={title}
            buttonProps={buttonProps}
            collapseBreakpoint={null}
            testId="local-recording-stop-button"
        />
    );
}
